import { z } from 'zod';

// Match row Zod schema — what apps/api returns from matches.get / matches.list
// AND what the operator UI uses to render a match card.
//
// Structurally a denormalized superset of MatchSearchResult: that one is
// optimized for the search dropdown (lightweight, name-only). MatchRow is
// the FULL view used by the match details page + the opportunity card
// (which embeds match info inline so the operator doesn't need a second
// round-trip when triaging an opp).

export const MATCH_STATUSES = [
  'scheduled',
  'live',
  'suspended',
  'completed',
  'cancelled',
  'retired',
  'walkover',
] as const;
export const MatchStatusSchema = z.enum(MATCH_STATUSES);
export type MatchStatus = z.infer<typeof MatchStatusSchema>;

export const SURFACE_KINDS = ['hard', 'clay', 'grass', 'carpet', 'indoor_hard'] as const;
export const SurfaceSchema = z.enum(SURFACE_KINDS);
export type Surface = z.infer<typeof SurfaceSchema>;

// liveState is a free-form JSONB — populated by the connector's WS handler
// when in-play. Front renders score + server only when state.kind === 'live'.
// Kept loose because api-tennis emits multiple shapes (set scores vs
// game-by-game) and we don't normalize aggressively yet.
export const MatchLiveStateSchema = z.record(z.unknown()).nullable();

export const MatchRowSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  // FK to tournaments.id; null when the connector didn't populate yet.
  tournamentId: z.string().uuid().nullable(),
  p1Id: z.string().uuid().nullable(),
  p2Id: z.string().uuid().nullable(),
  startsAt: z.string().datetime(),
  status: MatchStatusSchema,
  // Populated by tournament_cache lookup in fixture-sync. Lowercase canonical
  // form; loose string here so the operator UI can fall back to render the
  // raw value if api-tennis ever returns a new value we haven't normalized.
  surface: z.string().nullable(),
  bestOf: z.number().int().min(1).max(5),
  // Vendor ids by source ({api_tennis, oddspapi}) — front uses for "open in
  // source" affordance.
  vendorIds: z.record(z.string()).nullable(),
  liveState: MatchLiveStateSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Denormalized names for UI convenience. Populated by fixture-sync via the
  // canonical-id derivation. NULL only when the connector hasn't yet linked
  // the FK (rare; ~28% of historical matches have nulls until next sync
  // backfill — declines toward 0 over time).
  p1Name: z.string().nullable(),
  p2Name: z.string().nullable(),
  tournamentName: z.string().nullable(),
});
export type MatchRow = z.infer<typeof MatchRowSchema>;
